import { type DataPointSample, type FindingSeverity } from "@/lib/api/building";
import { dataPointLabel } from "@/lib/data-point-labels";
import {
  resolvedEquipmentZoneIds,
  type DataPoint,
  type EquipmentCard,
  type Zone,
} from "@/lib/building-domain";
import {
  equipmentTypeIcon,
  useEquipmentTypeMeta,
  useKbSensorTypes,
} from "@/lib/equipment-type-meta";
import { cn } from "@/lib/utils";
import { reliability_badge_class } from "../shared/_equipment-helpers";
import { moduleCard } from "@/lib/card-styles";
import { CardGlowOverlay } from "@/components/ui/card-glow-overlay";

// Freshness of the newest live reading — surfaces how recently the twin last saw this equipment,
// and flags a stale signal (older than 1 h). "Sensor" natures only; forecasts may be future-dated.
function live_freshness(
  data_points: DataPoint[],
): { label: string; stale: boolean } | null {
  const newest = data_points
    .filter((dp) => dp.nature !== "forecast" && dp.recorded_at)
    .map((dp) => new Date(dp.recorded_at!).getTime())
    .filter((t) => !Number.isNaN(t))
    .sort((a, b) => b - a)[0];
  if (newest == null) return null;

  const age_ms = Date.now() - newest;
  const mins = Math.floor(age_ms / 60000);
  const label =
    mins < 1
      ? "juuri nyt"
      : mins < 60
        ? `${mins} min sitten`
        : mins < 24 * 60
          ? `${Math.floor(mins / 60)} h sitten`
          : `${Math.floor(mins / (24 * 60))} vrk sitten`;
  return { label, stale: age_ms > 60 * 60 * 1000 };
}

// ── EquipmentMonitorCard — monitoring-mode card (operator=false) ──────────────

interface MonitorCardProps {
  equipment: EquipmentCard;
  zones: Zone[];
  behavior_labels: Record<string, string>;
  max_severity?: FindingSeverity;
  expanded: boolean;
  on_toggle: () => void;
}

export function EquipmentMonitorCard({
  equipment,
  zones,
  behavior_labels,
  max_severity,
  expanded,
  on_toggle,
}: MonitorCardProps) {
  const eqMeta = useEquipmentTypeMeta();
  const sensors = useKbSensorTypes();
  const reliability_dp = equipment.live_data_points.find(
    (dp) => dp.name === "reliability_score",
  );
  const reliability_score =
    reliability_dp?.numeric_value != null
      ? Math.round(reliability_dp.numeric_value)
      : null;

  const key_readings = equipment.live_data_points
    .filter(
      (dp) =>
        (dp.nature === "sensor" ||
          dp.nature === "setpoint" ||
          dp.nature === "actuator" ||
          dp.nature === "status" ||
          dp.nature === "calculated") &&
        dp.name !== "reliability_score" &&
        !dp.name.startsWith("behavior_") &&
        !dp.name.startsWith("kb_behavior_"), // compliance signals (0/0.5/1), not sensor readings
    )
    .slice(0, 4);
  const served_zones = resolvedEquipmentZoneIds(equipment)
    .map((id) => zones.find((z) => z.id === id))
    .filter((z): z is Zone => z !== undefined);

  const freshness = live_freshness(equipment.live_data_points);

  return (
    <button
      onClick={on_toggle}
      className={cn(
        moduleCard,
        "grid w-full cursor-pointer grid-rows-subgrid row-span-3 gap-2 p-4 text-left transition-all",
        expanded ? "border-primary/50 ring-1 ring-primary/30" : "",
      )}
    >
      <div>
        <div className="pointer-events-none absolute inset-0">
          <CardGlowOverlay />
        </div>
        <div className="relative flex items-start gap-2">
          <span className="mt-0.5 text-xl">
            {equipmentTypeIcon(eqMeta, equipment.equipment_type, sensors)}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold leading-tight">
              {equipment.name}
            </p>
            {equipment.equipment_tag && (
              <p className="font-mono text-xs text-muted-foreground">
                {equipment.equipment_tag}
              </p>
            )}
          </div>
          {reliability_score !== null && (
            <span
              className={cn(
                "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums",
                reliability_badge_class(reliability_score),
              )}
              title="Luotettavuuspisteet"
            >
              {reliability_score}%
            </span>
          )}
          {max_severity && (
            <span
              className={cn(
                "mt-1 h-2 w-2 shrink-0 rounded-full",
                max_severity === "critical"
                  ? "bg-destructive"
                  : max_severity === "warning"
                    ? "bg-warning"
                    : "bg-primary/40",
              )}
              title={
                max_severity === "critical"
                  ? "Kriittisiä havaintoja"
                  : max_severity === "warning"
                    ? "Varoituksia"
                    : "Huomioita"
              }
            />
          )}
        </div>
      </div>

      <div className="relative flex flex-col gap-0.5">
        {key_readings.length === 0 ? (
          <p className="text-xs text-muted-foreground">Ei mittaustietoa</p>
        ) : (
          key_readings.map((dp, i) => (
            <div key={i} className="flex justify-between text-xs">
              <span className="text-muted-foreground">
                {dataPointLabel(dp.name, behavior_labels)}
              </span>
              <span className="font-medium tabular-nums">{dp.value}</span>
            </div>
          ))
        )}
      </div>

      <div className="relative flex flex-wrap items-start gap-1.5 border-t border-border/30 pt-1.5 text-[11px] text-muted-foreground">
        <span className="font-medium">Palvelee vyöhykkeitä</span>
        {served_zones.length === 0 ? (
          <span>· —</span>
        ) : (
          served_zones.map((z) => (
            <span
              key={z.id}
              className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] text-primary"
            >
              {z.name}
            </span>
          ))
        )}
        {freshness && (
          <span
            className={cn(
              "ml-auto text-[10px] tabular-nums",
              freshness.stale ? "text-warning" : "text-muted-foreground",
            )}
            title={
              freshness.stale ? "Signaali vanhentunut" : "Viimeisin lukema"
            }
          >
            {freshness.stale ? "⚠ " : ""}
            {freshness.label}
          </span>
        )}
      </div>
    </button>
  );
}

// ── BehaviorSparkline ─────────────────────────────────────────────────────────

export function BehaviorSparkline({ samples }: { samples: DataPointSample[] }) {
  if (samples.length === 0) return null;

  const now = Date.now();
  const bucket_ms = 12 * 60 * 60 * 1000; // 12-hour buckets → 14 bars over 7 days
  const num_buckets = 14;
  const buckets: (number | null)[] = Array(num_buckets).fill(null);

  for (const s of samples) {
    if (s.numeric_value == null) continue;
    const age_ms = now - new Date(s.recorded_at).getTime();
    const bucket = num_buckets - 1 - Math.floor(age_ms / bucket_ms);
    if (bucket < 0 || bucket >= num_buckets) continue;
    const prev = buckets[bucket];
    buckets[bucket] =
      prev == null ? s.numeric_value : Math.min(prev, s.numeric_value);
  }

  return (
    <div
      className="flex items-end gap-0.5 h-4"
      title="7 vrk käyttäytymishistoria (12h jaksoissa)"
    >
      {buckets.map((v, i) => {
        const color =
          v == null
            ? "bg-muted-foreground/20"
            : v >= 1.0
              ? "bg-success/70"
              : v <= 0.0
                ? "bg-red-500/70"
                : "bg-muted-foreground/30";
        const h = v == null ? 25 : v >= 1.0 ? 100 : v <= 0.0 ? 100 : 50;
        return (
          <div
            key={i}
            className={cn("w-1.5 rounded-sm transition-all", color)}
            style={{ height: `${h}%` }}
          />
        );
      })}
    </div>
  );
}
